import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { getConsentStatus, saveConsent, type ConsentStatus } from "@/lib/analytics";

/**
 * Cookie consent banner for GDPR compliance
 *
 * Visas endast på publika sidor och endast om användaren
 * inte redan har gjort ett val.
 */
export function CookieConsent() {
  const [status, setStatus] = useState<ConsentStatus>("pending");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check consent status on mount
    const currentStatus = getConsentStatus();
    setStatus(currentStatus);

    // Show banner after a short delay if no choice has been made
    if (currentStatus === "pending") {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    saveConsent(true);
    setStatus("granted");
    setIsVisible(false);
  };

  const handleDecline = () => {
    saveConsent(false);
    setStatus("denied");
    setIsVisible(false);
  };

  // Don't render if already decided or not visible
  if (status !== "pending" || !isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 animate-in slide-in-from-bottom duration-500">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="hidden sm:flex w-12 h-12 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-primary-600" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Vi använder cookies
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Vi använder cookies för att analysera hur vår webbplats används och förbättra din upplevelse.
                  Genom att klicka "Acceptera" samtycker du till vår användning av analytiska cookies.{" "}
                  <Link
                    to="/cookies"
                    className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-2"
                  >
                    Läs mer om cookies
                  </Link>
                </p>
              </div>

              {/* Close button (mobile) */}
              <button
                onClick={handleDecline}
                className="sm:hidden p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Stäng"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-5 sm:mt-4 sm:ml-16">
              <button
                onClick={handleAccept}
                className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg hover:from-primary-700 hover:to-primary-800 shadow-sm transition-all duration-200 text-sm"
              >
                Acceptera
              </button>
              <button
                onClick={handleDecline}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Endast nödvändiga
              </button>
              <Link
                to="/cookies"
                className="hidden sm:inline-flex px-5 py-2.5 text-gray-500 hover:text-gray-700 font-medium transition-colors text-sm"
              >
                Läs mer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
